import React, { useState } from "react";
import { Modal } from "@mui/material";
import { GET_TRADE_POST_OPTIONS } from "./queryKey";
import { getTradePostOptions } from "../../Helpers/backendHelper";
import { useQuery, UseQueryResult } from "react-query";
import { TradeOptionsResponse } from "../../Helpers/backendResponseInterfaces";
import CircularProgress from "@mui/material/CircularProgress";
import { useCreateTradePostMutation } from "../misc/mutations";

interface TopBarProps {
  handleClose: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ handleClose }) => (
  <div className="flex flex-row items-center justify-between w-full px-2 basis-1/12">
    <div className="flex flex-row items-start justify-center gap-2">
      <div className="font-semibold text-gray-600">Trade</div>
    </div>
    <div className="flex flex-row items-end justify-center gap-2">
      <button
        className={`rounded-xl py-1/2 px-3 
          bg-red-300 text-red-600
          hover:text-white hover:bg-red-500
          transition-all duration-300
          `}
        onClick={handleClose}
      >
        Cancel
      </button>
    </div>
  </div>
);

interface ChildModalProps {
  onClose: (confirmed: boolean) => void;
}

const ChildModal: React.FC<ChildModalProps> = ({ onClose }) => (
  <div className="fixed bg-red-100 w-fit h-fit p-4 rounded-lg flex flex-col justify-center items-center max-w-64">
    <div className="text-lg font-light text-red-700 text-center">
      You Sure?
      <br />
      you will have to reshare this information
    </div>
    <div className="flex flex-row items-center justify-center gap-2 mt-4">
      <button
        className={`rounded-xl py-1/2 px-3
          bg-gray-300 text-white
          hover:text-white hover:bg-red-500
          transition-all duration-300
          `}
        onClick={() => onClose(true)}
      >
        Yes
      </button>
      <button
        className={`rounded-xl py-1/2 px-3
          bg-blue-300 text-blue-600
          hover:text-white hover:bg-blue-500
          transition-all duration-300
          `}
        onClick={() => onClose(false)}
      >
        No
      </button>
    </div>
  </div>
);

interface SelectProps {
  items: string[];
  selectedItem: string;
  setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
  borderColor?: string; // Optional: to handle different border colors if needed
}

const Select: React.FC<SelectProps> = ({
  items,
  selectedItem,
  setSelectedItem,
  borderColor = "blue", // Default border color
}) => (
  <div className="flex flex-row items-start gap-2 w-full">
    {items.map((item) => (
      <div
        key={item}
        className={`p-1 rounded-lg hover:bg-slate-100 cursor-pointer border transition-all duration-300 ${
          item === selectedItem
            ? `border-${borderColor}-100 font-semibold bg-slate-200 border-0 border-b-${borderColor}-400 border-b-4`
            : `border-${borderColor}-200 hover:bg-slate-300`
        }`}
        onClick={() => setSelectedItem(item)}
      >
        {item}
      </div>
    ))}
  </div>
);

interface SelectOptionProps {
  selectedOption: string;
  optionTypes: string[];
  strikePrice: number | undefined;
  strikePriceNumDigits: { [key: string]: number };
  index: string;
  setOptionType: React.Dispatch<React.SetStateAction<string>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
  noStrikePriceMessage?: string;
}

const SelectOption: React.FC<SelectOptionProps> = ({
  selectedOption,
  optionTypes,
  strikePrice,
  strikePriceNumDigits,
  index,
  setOptionType,
  handleChange,
  handleBlur,
  noStrikePriceMessage,
}) => (
  <div className="flex flex-row items-center justify-start gap-2 w-full animate-fadeIn">
    <div>
      <input
        placeholder="Strike Price"
        className="p-1 rounded-lg border border-gray-200 focus:bg-white bg-transparent focus:outline-none"
        value={strikePrice}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {noStrikePriceMessage && (
        <div className="text-red-500 text-xs">{noStrikePriceMessage}</div>
      )}
    </div>
    {optionTypes.map((optionType) => (
      <div
        key={optionType}
        className={`p-1 rounded-lg cursor-pointer border transition-all duration-300 ${
          optionType === selectedOption
            ? `border-gray-100 font-semibold bg-slate-200 ${
                selectedOption === "Call"
                  ? "border-b-green-400"
                  : "border-b-red-600"
              } border-b-4`
            : "border-gray-200 hover:bg-slate-300"
        }`}
        onClick={() => setOptionType(optionType)}
      >
        {optionType}
      </div>
    ))}
  </div>
);

interface EntryTargetStopLossProps {
  entry: number | undefined;
  setEntry: (e: React.ChangeEvent<HTMLInputElement>) => void;
  target: number | undefined;
  setTarget: (e: React.ChangeEvent<HTMLInputElement>) => void;
  stopLoss: number | undefined;
  setStopLoss: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedTradeType: string;
  setTradeType: React.Dispatch<React.SetStateAction<string>>;
  tradeTypes: string[];
  noEntryMessage?: string;
  noSLMessage?: string;
}

const EntryTargetStopLoss: React.FC<EntryTargetStopLossProps> = ({
  entry,
  setEntry,
  target,
  setTarget,
  stopLoss,
  setStopLoss,
  selectedTradeType,
  setTradeType,
  tradeTypes,
  noEntryMessage,
  noSLMessage,
}) => (
  <div className="flex md:flex-row flex-col md:items-center items-start justify-between w-full gap-4">
    <div className="flex flex-row items-center justify-start gap-2">
      <div>
        <input
          placeholder="Entry Price"
          className="p-1 rounded-lg border border-gray-200 focus:bg-white bg-transparent focus:outline-none"
          value={entry}
          onChange={setEntry}
        />
        {noEntryMessage && (
          <div className="text-red-500 text-xs">{noEntryMessage}</div>
        )}
      </div>
      {tradeTypes.map((tradeType) => (
        <div
          key={tradeType}
          className={`p-1 rounded-lg cursor-pointer border transition-all duration-300 ${
            tradeType === selectedTradeType
              ? `border-gray-100 font-semibold bg-slate-200 ${
                  tradeType === "Buy"
                    ? "border-b-green-400"
                    : "border-b-red-600"
                } border-b-4`
              : "border-gray-200 hover:bg-slate-300"
          }`}
          onClick={() => setTradeType(tradeType)}
        >
          {tradeType}
        </div>
      ))}
    </div>
    <div>
      <input
        placeholder="Stop Loss"
        className="p-1 rounded-lg border border-red-200 focus:bg-white bg-transparent focus:outline-none"
        value={stopLoss}
        onChange={setStopLoss}
      />
      {noEntryMessage && (
        <div className="text-red-500 text-xs">{noSLMessage}</div>
      )}
    </div>
    <input
      placeholder="Target Price"
      className="p-1 rounded-lg border border-green-200 focus:bg-white bg-transparent focus:outline-none"
      value={target}
      onChange={setTarget}
    />
  </div>
);

interface MakePostProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPosts: () => void;
}

export const MakeTradePost: React.FC<MakePostProps> = ({
  isOpen,
  setIsOpen,
  resetPosts,
}) => {
  const [index, setIndex] = useState<string>("");
  const [instrumentType, setInstrumentType] = useState<string>("");
  const [expiryType, setExpiryType] = useState<string>("");
  const [strikePrice, setStrikePrice] = useState<number>();
  const [optionType, setOptionType] = useState<string>("");
  const [tradeType, setTradeType] = useState<string>("Buy");
  const [entry, setEntry] = useState<number>();
  const [target, setTarget] = useState<number>();
  const [stopLoss, setStopLoss] = useState<number>();
  const [noEntryMessage, setNoEntryMessage] = useState<string>("");
  const [noSLMessage, setNoSLMessage] = useState<string>("");
  const [noStrikePriceMessage, setNoStrikePriceMessage] = useState<string>("");
  const [postTradeOptions, setPostTradeOptions] =
    useState<TradeOptionsResponse>();

  const isSubmittable = () => !!entry && !!stopLoss && !!instrumentType;
  const resetErrorMessages = () => {
    setNoEntryMessage("");
    setNoSLMessage("");
    setNoStrikePriceMessage("");
  };

  const { isLoading } = useQuery({
    queryKey: GET_TRADE_POST_OPTIONS,
    queryFn: getTradePostOptions,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onSuccess: (tradeData: { data: TradeOptionsResponse }) => {
      const data = tradeData.data;
      setIndex(data.index_types[0]);
      setInstrumentType(data.instrument_types[0]);
      setExpiryType(data.expiry_types[0]);
      setOptionType(data.option_types[0]);
      setPostTradeOptions(data);
    },
  }) as UseQueryResult<TradeOptionsResponse, unknown>;

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const resetValues = () => {
    setEntry(undefined);
    setTarget(undefined);
    setStopLoss(undefined);
    resetErrorMessages();
  };

  const submitTradeSuccess = () => {
    resetPosts();
    setIsOpen(false);
  };

  const createTradePost = useCreateTradePostMutation(
    submitTradeSuccess,
    resetPosts,
  );

  const onSubmitPost = () => {
    if (!entry || entry === 0) setNoEntryMessage("Please give an Entry price");
    if (!stopLoss || entry === 0) setNoSLMessage("Please give a Stop loss");
    if (instrumentType === "Option" && (!strikePrice || strikePrice === 0))
      setNoStrikePriceMessage("Strike price is required.");
    if (
      entry &&
      stopLoss &&
      (instrumentType !== "Option" ||
        (instrumentType === "Option" && strikePrice))
    ) {
      const tradeData = {
        index,
        instrument_type: instrumentType,
        option_type: optionType,
        strike_price: strikePrice,
        trade_type: tradeType,
        entry: entry,
        target: target,
        stop_loss: stopLoss,
      };
      createTradePost.mutate({
        tradeData: tradeData,
        postType: "Trade",
      });
      resetValues();
    }
  };

  const handleClose = () => {
    if (entry || stopLoss || (instrumentType === "Option" && strikePrice)) {
      setShowConfirmation(true);
    } else {
      resetValues();
      setIsOpen(false);
    }
  };

  const handleConfirmationClose = (confirmed: boolean) => {
    setShowConfirmation(false);
    if (confirmed) {
      resetValues();
      setIsOpen(false);
    }
  };

  const handleChange =
    (setState: React.Dispatch<React.SetStateAction<number | undefined>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^\d*$/.test(value)) {
        setNoStrikePriceMessage("");
        setNoEntryMessage("");
        setNoSLMessage("");
        setState(Number(value));
      }
    };

  const handleBlur = () => {
    if (postTradeOptions?.strike_price_num_digits && strikePrice) {
      setStrikePrice(
        Number(
          strikePrice
            .toString()
            .padEnd(postTradeOptions?.strike_price_num_digits[index], "0"),
        ),
      );
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      onKeyDown={(e) => e.key === "Enter" && onSubmitPost()}
    >
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-slate-100 md:w-1/2 md:min-w-fit w-5/6  max-h-3/4 p-4 rounded-lg flex flex-col justify-center items-center">
        <TopBar handleClose={handleClose} />
        {isLoading ? (
          <CircularProgress />
        ) : (
          <div className="flex flex-col gap-4 items-center justify-start rounded-lg w-full my-12">
            {postTradeOptions?.index_types && (
              <Select
                items={postTradeOptions.index_types}
                selectedItem={index}
                setSelectedItem={setIndex}
              />
            )}
            {postTradeOptions?.instrument_types && (
              <Select
                items={postTradeOptions.instrument_types}
                selectedItem={instrumentType}
                setSelectedItem={setInstrumentType}
              />
            )}
            {postTradeOptions && instrumentType !== "Cash" && (
              <Select
                items={postTradeOptions?.expiry_types}
                selectedItem={expiryType}
                setSelectedItem={setExpiryType}
              />
            )}
            {instrumentType === "Option" && (
              <SelectOption
                selectedOption={optionType}
                optionTypes={postTradeOptions?.option_types || []}
                strikePrice={strikePrice}
                strikePriceNumDigits={
                  postTradeOptions?.strike_price_num_digits || { na: 0 }
                }
                index={index}
                setOptionType={setOptionType}
                handleChange={handleChange(setStrikePrice)}
                handleBlur={handleBlur}
                noStrikePriceMessage={noStrikePriceMessage}
              />
            )}
            <EntryTargetStopLoss
              entry={entry}
              setEntry={handleChange(setEntry)}
              target={target}
              setTarget={handleChange(setTarget)}
              stopLoss={stopLoss}
              setStopLoss={handleChange(setStopLoss)}
              selectedTradeType={tradeType}
              setTradeType={setTradeType}
              tradeTypes={postTradeOptions?.trade_types || []}
              noEntryMessage={noEntryMessage}
              noSLMessage={noSLMessage}
            />
          </div>
        )}
        <button
          className={`rounded-xl py-3 px-6
            ${
              isSubmittable()
                ? "bg-green-300 text-green-600 hover:text-white hover:bg-green-500"
                : "bg-slate-300 text-slate-600 cursor-not-allowed"
            }
            transition-all duration-300
            `}
          onClick={onSubmitPost}
          disabled={!isSubmittable()}
        >
          Trade!
        </button>
        {showConfirmation && <ChildModal onClose={handleConfirmationClose} />}
      </div>
    </Modal>
  );
};
